import React, { useEffect, useRef, useState } from 'react';
import { Repeat, MessageSquareWarning, Database, HelpCircle, ShieldCheck, Lock, Eye } from 'lucide-react';
import { Reveal } from '@/components/site/Reveal';
import { CTABand } from '@/components/site/CTABand';
import { FAQList } from '@/components/site/FAQ';
import { FaqSchema } from '@/components/site/Seo';
import { HERO_IMAGES } from '@/data/heroImages';
import {
  ServiceHero, ProblemsBlock, BuildBlock, ApproachBlock,
  OutcomesBlock, RelatedServices, PageLinks, SectionHead, SECTION, WRAP,
} from '@/components/site/ServicePageParts';
import { SITE } from '@/data/site';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROBLEMS = [
  { slug: 'manual-work', icon: Repeat, title: 'Repetitive manual work', body: 'Tagging, extracting, summarising, routing. The kind of work nobody learned a degree for. AI is great at the boring 80% so your team can own the interesting 20%.' },
  { slug: 'slow-support', icon: MessageSquareWarning, title: 'Slow customer support', body: 'Median first response on email is 12 hours; on chat it should be 30 seconds. A grounded assistant answers the 60% that are obvious, escalates the rest.' },
  { slug: 'unused-data', icon: Database, title: 'Data collected but never used', body: 'Two terabytes of tickets, calls and PDFs—and nobody opens them. We turn them into the brain of an assistant that actually knows your business.' },
  { slug: 'no-direction', icon: HelpCircle, title: 'Not knowing where AI even helps', body: 'Most of the work is choosing the right problem. We run a 1-week feasibility audit before any line of code—and tell you when AI is not the answer.' },
];

const BUILD = [
  { slug: 'assistants', tag: 'Assistant · Chat', title: 'AI assistants & chatbots', body: 'Grounded chat assistants for support, sales, internal Q&A. Backed by your docs, your tone, your guardrails. Not another GPT wrapper.', accent: '#6C5CE7' },
  { slug: 'agents', tag: 'Agent · Automation', title: 'Workflow automation & agents', body: 'Multi-step agents that read, decide and act—within tight tool scopes. Triage, drafting, follow-up, reporting.', accent: '#4B9FFF' },
  { slug: 'doc-processing', tag: 'Docs · Extract', title: 'Document & data processing', body: 'Pull structured data out of PDFs, invoices, forms, contracts. Human-in-the-loop where it matters; full auto where it doesn’t.', accent: '#6C5CE7' },
  { slug: 'recommendations', tag: 'Personalisation', title: 'Recommendations & personalization', body: 'Editorial + collaborative recos for stores, content sites and SaaS. Trained on your data, owned by you.', accent: '#4B9FFF' },
  { slug: 'predictive', tag: 'ML · Predictive', title: 'Predictive analytics', body: 'Churn, propensity, demand—built as predictions inside the products and dashboards your team already opens.', accent: '#4B9FFF' },
  { slug: 'features', tag: 'Embedded · SDK', title: 'AI features inside existing products', body: 'Summarise, rewrite, classify, search—dropped into the screens your users already use. Latency-budgeted, eval-gated, cost-capped.', accent: '#6C5CE7' },
  { slug: 'kb-rag', tag: 'RAG · Knowledge', title: 'Assistants grounded in your knowledge base', body: 'RAG done seriously: chunking strategy, hybrid search, citation-first answers, refusal-when-unsure behaviour, evals.', accent: '#6C5CE7' },
];

const APPROACH = [
  { slug: 'discovery', step: '01', title: 'Use-case discovery & feasibility', body: 'A one-week audit. We map the top five candidates, score them on impact and feasibility, and tell you—honestly—whether AI is the right tool for each.' },
  { slug: 'data-readiness', step: '02', title: 'Data readiness', body: 'Inventory your sources, audit quality, decide on chunking + embedding strategy. The 80% of an AI project that nobody films a demo of.' },
  { slug: 'prototype', step: '03', title: 'Prototype / POC', body: 'A scoped POC in two to four weeks. Live, evaluable, not a slide deck. Real users on it before week five.' },
  { slug: 'integration', step: '04', title: 'Integration', body: 'Into your product surface, your CRM, your ops console. We do not believe in standalone "AI portals" unless that is literally the product.' },
  { slug: 'evaluation', step: '05', title: 'Evaluation & guardrails', body: 'Eval suites with ground-truth examples, refusal tests, jailbreak tests, latency + cost budgets. Quality is a number, not a vibe.' },
  { slug: 'deployment', step: '06', title: 'Deployment', body: 'A staged rollout: shadow → 5% → 25% → 100%. Kill-switches, fallback to deterministic logic, on-call playbook.' },
  { slug: 'monitoring', step: '07', title: 'Monitoring & improvement', body: 'Observability for prompts, citations, costs, refusals. A monthly review cadence with model + prompt changes shipped behind feature flags.' },
];

const FAQ = [
  { q: 'How do you keep our data safe?', a: 'Two layers. First: the assistant only sees the documents you gate it to—nothing else. Second: we use providers with no-training clauses, encrypt at rest, and offer self-hosted embeddings or open-source models when policy demands it.' },
  { q: 'Will it hallucinate?', a: 'Less than you think, if it is built right. We use citation-first prompts, retrieval-grounded answers, refusal heuristics, and an eval suite that catches regressions before deploy.' },
  { q: 'Can it run on our own infrastructure?', a: 'Yes. We deploy to your AWS / GCP / Azure account. For sensitive workloads we run open-source models (Llama, Mistral, Qwen) on your own GPUs.' },
  { q: 'How much will it cost to run?', a: 'We set a per-conversation cost ceiling in the design phase and stay under it. For a grounded support assistant, $0.01–$0.04 per resolved query is typical at production volumes.' },
  { q: 'Which models do you use?', a: 'Whichever one matches the job. Claude Sonnet / Opus for reasoning-heavy work, GPT-5 family for tool use + structured output, Gemini for very large context, open models when policy or cost demands it.' },
  { q: 'How long to ship a v1?', a: 'A scoped POC is 2–4 weeks. A production-grade assistant with evals, guardrails and monitoring is 8–12 weeks.' },
];

const OUTCOMES = [
  { kpi: 64, suffix: '%', label: 'queries auto-resolved (median)' },
  { kpi: 3800, suffix: '+', label: 'support hrs saved / yr / team' },
  { kpi: 92, suffix: '%', label: 'eval accuracy on ground truth' },
  { kpi: 1.4, suffix: 's', label: 'p50 first token (RAG)' },
];

const RESP_AI = [
  { icon: ShieldCheck, k: 'No training on your data', v: 'We only use vendors with no-training contractual clauses. Your data is yours. Period.' },
  { icon: Lock, k: 'PII redaction in-flight', v: 'Names, phone numbers, IDs and free-text are scanned before they ever reach a model. Configurable per field.' },
  { icon: Eye, k: 'Citations or it didn’t happen', v: 'Every grounded answer carries citations to the source chunks. Users can audit. Reviewers can verify.' },
];

const SUGGESTIONS = [
  'What does NioDelta actually do?',
  'How long does a v1 AI assistant take?',
  'Can you work with our existing tech stack?',
  'How is this different from buying ChatGPT Enterprise?',
];

/* ----- Signature: live mini-assistant ----- */
function MiniAssistant() {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi — I'm NioBot, a grounded mini-assistant. Ask me anything about how NioDelta works." },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async (text) => {
    if (busy) return;
    const t = (text ?? input).trim();
    if (!t) return;
    setInput('');
    const history = messages.slice(-6).map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: 'user', content: t }, { role: 'assistant', content: '' }]);
    setBusy(true);

    try {
      const res = await fetch(`${API}/ai/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: t, session_id: sessionId, history }),
      });
      if (!res.ok || !res.body) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        setMessages((prev) => {
          const next = prev.slice();
          next[next.length - 1] = { role: 'assistant', content: buf };
          return next;
        });
      }
      if (!sessionId) setSessionId(`niobot-${Date.now()}`);
    } catch (err) {
      setMessages((prev) => {
        const next = prev.slice();
        next[next.length - 1] = {
          role: 'assistant',
          content: `Sorry — the assistant is temporarily offline. WhatsApp a partner instead: ${SITE.whatsapp}`,
        };
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="ai-assistant"
      className="rounded-[18px] border border-white/[0.08] bg-ink-surface overflow-hidden flex flex-col h-[600px] max-h-[80vh]"
    >
      {/* header */}
      <div className="px-5 md:px-7 py-5 border-b border-white/[0.08] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full border border-white/15 grid place-items-center">
            <svg viewBox="0 0 32 32" className="h-4 w-4" aria-hidden><path d="M16 6 L26 24 L6 24 Z" fill="none" stroke="#FFFFFF" strokeWidth="2"/></svg>
          </div>
          <div>
            <div className="font-display text-[17px] font-semibold tracking-[-0.03em]">NioBot</div>
            <div className="mt-0.5 text-[12px] text-ink-muted">Grounded in NioDelta&apos;s services · live demo</div>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2.5 nd-mono !text-[10px]">
          <span className="nd-live" aria-hidden />
          online
        </div>
      </div>

      {/* messages */}
      <div ref={scrollRef} data-testid="ai-messages" className="flex-1 overflow-y-auto p-5 md:p-7 space-y-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] md:max-w-[70%] rounded-[18px] px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap break-words ${
                m.role === 'user'
                  ? 'bg-white text-ink-bg rounded-br-[6px]'
                  : 'bg-ink-bg border border-white/[0.08] text-ink-text rounded-bl-[6px]'
              }`}
            >
              {m.content || (m.role === 'assistant' && busy && i === messages.length - 1
                ? <span className="inline-flex gap-1 items-center text-ink-muted">
                    <span className="h-1.5 w-1.5 rounded-full bg-white/60 animate-bounce" />
                    <span className="h-1.5 w-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: '120ms' }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: '240ms' }} />
                  </span>
                : null)}
            </div>
          </div>
        ))}
      </div>

      {/* suggestions */}
      {messages.length <= 1 && (
        <div className="px-5 md:px-7 pb-4 flex flex-wrap gap-2">
          {SUGGESTIONS.map((s, i) => (
            <button
              key={s}
              type="button"
              data-testid={`ai-suggest-${i}`}
              onClick={() => send(s)}
              className="text-[13px] px-3.5 py-2 rounded-full border border-white/[0.12] text-white/85 hover:border-white hover:bg-white hover:text-ink-bg transition-colors duration-300"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* input */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="px-4 md:px-5 py-4 border-t border-white/[0.08] flex items-center gap-2"
      >
        <input
          data-testid="ai-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask NioBot anything about our services…"
          disabled={busy}
          className="input flex-1"
        />
        <button
          type="submit"
          data-testid="ai-send"
          disabled={busy || !input.trim()}
          className="group shrink-0 inline-flex items-center gap-2.5 rounded-full bg-white text-ink-bg pl-5 pr-1.5 py-1.5 text-sm font-medium transition-opacity duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? 'Thinking…' : 'Send'}
          <span aria-hidden className="h-8 w-8 rounded-full bg-ink-bg text-white grid place-items-center">
            <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" className="transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:-rotate-45"><path d="M2 8h12M9 3l5 5-5 5" /></svg>
          </span>
        </button>
      </form>
    </div>
  );
}

const AUTOMATION = [
  { k: 'Document processing', v: 'Read invoices, purchase orders, KYC documents and forms, extract the fields, and push them into your ERP or CRM with a human check on low-confidence items.' },
  { k: 'Enquiry & lead handling', v: 'Classify incoming WhatsApp, email and website enquiries, draft a reply from your own price list and policies, and route the lead to the right salesperson.' },
  { k: 'Support automation', v: 'Answer routine customer questions from your documents and order data, and hand over to a person with the full context when the question is not routine.' },
  { k: 'Reports & summaries', v: 'Turn daily sales, production or attendance data into a short written summary sent to managers each morning.' },
  { k: 'Workflow agents', v: 'Multi-step tasks such as follow-ups, reconciliations and data entry across tools, run with approval steps, logs and spending limits.' },
];

export default function AISolutions() {
  return (
    <div data-testid="ai-solutions-page">
      <FaqSchema items={FAQ} />
      <ServiceHero
        image={HERO_IMAGES['ai-solutions']}
        serviceNum="06"
        serviceTitle="AI Solutions"
        headlineText="AI that does real work for your business."
        accent="work"
        description="RAG, agents, document processing and AI features inside existing products. Built with evals, guardrails and a cost ceiling—not a vibe. The kind of AI you can sign a contract against."
        secondaryCta={{ label: 'Try the assistant below', to: '#ai-try' }}
        testIdPrefix="ai"
      />

      <ProblemsBlock
        heading="Four shapes of AI-shaped pain."
        accentWord="AI-shaped pain."
        intro="If one of these sounds familiar, AI probably has a contribution to make. If none of them do, we&apos;ll tell you that too—on the first call."
        items={PROBLEMS}
        testIdPrefix="ai"
      />

      <BuildBlock
        heading="Seven shapes of AI we put into production."
        accentWord="production."
        intro="Not every problem is a chatbot. We pick the shape that actually moves the metric."
        items={BUILD}
        testIdPrefix="ai"
      />

      <ApproachBlock
        heading="Seven stages. One eval suite."
        accentWord="eval suite."
        intro="Quality is a number, not a vibe. Every step of our approach is anchored to a metric you can read in a board meeting."
        steps={APPROACH}
        testIdPrefix="ai"
      />

      {/* Signature: try the assistant */}
      <section id="ai-try" data-testid="ai-try" className={`${SECTION} scroll-mt-24`}>
        <div className={WRAP}>
          <SectionHead
            num="05"
            label="Try it · Live mini-assistant"
            heading="Ask NioBot a question. Streaming response."
            accent="Streaming response."
            intro={(
              <>
                A grounded mini-assistant wired to <code className="font-mono text-[14px] text-white">/api/ai/chat/stream</code> on this site. It only answers about NioDelta&apos;s services—and refuses what it doesn&apos;t know.
              </>
            )}
          />
          <Reveal className="lg:pl-[252px]">
            <MiniAssistant />
          </Reveal>
        </div>
      </section>

      {/* Responsible AI */}
      <section data-testid="ai-responsible" className={SECTION}>
        <div className={WRAP}>
          <SectionHead
            num="06"
            label="Responsible AI"
            heading="The boring slide we made interesting."
            accent="interesting."
            intro="Trust is the conversion lever for AI work. We design for it on day one—not as a launch checklist."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.08] rounded-[18px] overflow-hidden ring-1 ring-white/[0.08]">
            {RESP_AI.map((r, i) => {
              const Icon = r.icon;
              return (
                <Reveal key={r.k} delay={i * 70} className="bg-ink-bg">
                  <div data-testid={`ai-resp-${i}`} className="h-full min-h-[280px] p-8 md:p-10 flex flex-col">
                    <div className="flex items-center justify-between">
                      <span aria-hidden className="h-12 w-12 rounded-full border border-white/10 grid place-items-center text-[#8C7CFF]">
                        <Icon size={18} strokeWidth={1.5} />
                      </span>
                      <span className="nd-mono !text-[11px]">{String(i + 1).padStart(2, '0')}</span>
                    </div>
                    <h3 className="mt-auto pt-14 font-display text-[28px] md:text-[32px] font-semibold tracking-[-0.04em] leading-none">{r.k}</h3>
                    <p className="mt-4 text-ink-muted leading-relaxed text-[15px]">{r.v}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <OutcomesBlock
        heading="Numbers we’d sign a contract against."
        accentWord="contract"
        intro="Medians across the last 7 AI engagements (2024–25). The boring metrics, on purpose—latency, accuracy, savings."
        values={OUTCOMES}
        testIdPrefix="ai"
      />

      <section data-testid="ai-automation" className={SECTION}>
        <div className={WRAP}>
          <SectionHead
            num="08"
            label="AI automation"
            heading="AI automation for business operations."
            accent="business operations."
            intro="Not every AI project is a chatbot. Often the best return is removing a repetitive step from work your team already does."
          />
          <ul className="border-t border-white/[0.08]">
            {AUTOMATION.map((a, i) => (
              <Reveal as="li" key={a.k} delay={i * 60} className="border-b border-white/[0.08]">
                <div className="group relative isolate grid grid-cols-[56px_1fr] lg:grid-cols-[220px_1fr_1fr] gap-x-8 gap-y-3 py-8 md:py-10">
                  <span aria-hidden className="absolute inset-y-0 -inset-x-4 md:-inset-x-12 -z-10 origin-bottom scale-y-0 bg-[linear-gradient(90deg,rgba(108,92,231,.10),rgba(75,159,255,.03))] transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:scale-y-100" />
                  <span className="nd-mono pt-2 tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="font-display text-2xl md:text-[32px] font-semibold tracking-[-0.04em] leading-tight transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:translate-x-2">{a.k}</h3>
                  <p className="col-start-2 lg:col-start-3 text-ink-muted leading-relaxed max-w-[52ch] lg:pt-1.5">{a.v}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section data-testid="ai-faq" className={SECTION}>
        <div className={WRAP}>
          <SectionHead num="09" label="FAQ" heading="Things we're asked before the call." accent="before" />
          <div className="lg:pl-[252px]">
            <FAQList items={FAQ} testIdPrefix="ai-faq" />
          </div>
        </div>
      </section>

      <PageLinks
        heading="Where AI fits with the rest of the build"
        links={[
          { to: '/services/whatsapp-chatbot-development/', label: 'WhatsApp chatbot development' },
          { to: '/services/custom-software-development/', label: 'Custom software development' },
          { to: '/services/mobile-app-development/', label: 'Mobile app development' },
          { to: '/website-development-cost/', label: 'AI chatbot and automation pricing' },
        ]}
        testIdPrefix="ai"
      />

      <RelatedServices exclude="ai-solutions" testIdPrefix="ai" num="10" />

      <CTABand
        eyebrow="Have a use-case in mind?"
        headline="Tell us what AI is supposed to do."
        sub="A 30-minute WhatsApp call with a partner. We&apos;ll be honest about whether AI is the right tool for your problem."
      />
    </div>
  );
}
